// constants/songRoles.js
// Centralized song role definitions for Mass events

import { useLanguage } from '../contexts/LanguageContext';

/**
 * Mass song role values (stored in database)
 * These are kept as Slovenian text for backwards compatibility with existing events
 */
export const MASS_ROLE_VALUES = [
  '',
  'Vstopna',
  'Gospod, usmili se',
  'Slava',
  'Psalm',
  'Aleluja',
  'Darovanje',
  'Svet',
  'Jagnje Božje',
  'Obhajilo',
  'Zaključna',
  'Dodatna'
];

/**
 * Default role for new songs added to any setlist
 * Empty string means no role assigned
 */
export const DEFAULT_SONG_ROLE = '';

/**
 * Gets the localized display label for a song role
 * @param {string} roleValue - The role value (stored in DB)
 * @param {function} t - Translation function from i18next
 * @returns {string} The localized display label
 */
export const getRoleLabel = (roleValue, t) => {
  if (!roleValue) return t('songs.roles.none');

  // Map Slovenian values to translation keys
  const roleKeyMap = {
    'Vstopna': 'vstopna',
    'Gospod, usmili se': 'gospodUsmiliSe',
    'Slava': 'slava',
    'Psalm': 'psalm',
    'Aleluja': 'aleluja',
    'Darovanje': 'darovanje',
    'Svet': 'svet',
    'Jagnje Božje': 'jagnjeBozje',
    'Obhajilo': 'obhajilo',
    'Zaključna': 'zakljucna',
    'Dodatna': 'dodatna'
  };

  const translationKey = roleKeyMap[roleValue];
  if (translationKey) {
    return t(`songs.roles.${translationKey}`);
  }

  // Fallback to original value for custom roles or old data
  return roleValue;
};

/**
 * Generates mass role options with localized labels for select components
 * @param {function} t - Translation function from i18next
 * @returns {Array} Array of {value, label} objects
 */
export const getMassRoleOptions = (t) => {
  return MASS_ROLE_VALUES.map(value => ({
    value,
    label: getRoleLabel(value, t)
  }));
};

/**
 * Component for rendering song role selector for Mass events
 * @param {Object} props - Component props
 * @param {string} props.value - Current role value
 * @param {function} props.onChange - Change handler
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Inline styles
 * @returns {JSX.Element} Role selector component
 */
export const MassRoleSelector = ({ value, onChange, className = '', style = {}, ...props }) => {
  const { t } = useLanguage();
  const roleOptions = getMassRoleOptions(t);

  return (
    <select
      className={`form-select ${className}`}
      style={style}
      value={value || ''}
      onChange={onChange}
      {...props}
    >
      {roleOptions.map(role => (
        <option key={role.value} value={role.value}>
          {role.label}
        </option>
      ))}
    </select>
  );
};